import ethCoinSvg from '@raison/static/icons/eth-coin-line.svg';
import {color} from '@raison/ui-kit/theme';
import copyToClipboard from 'clipboard-copy';
import {QRCodeSVG} from 'qrcode.react';
import React, {useMemo, useState} from 'react';
import {stablecoins} from '~/config';
import {NotFoundPage} from '~/entities/fallbacks';
import {Decoder} from '@raison/prelude';
import {AccountId, Source} from '~/library/types';
import {RootLayout, RsnCopyIcon, RsnMessage, Skeleton, styles, useBoolean} from '~/library/ui-kit';

export interface OwnWalletResource {
    currency: string;
    address: string;
    icon: Source | null;
}

type ResourceLike<T> = {
    data?: T | undefined;
    isLoading: boolean;
    isError: boolean;
};

const AccountIdFromString = Decoder.prepare(Number)(AccountId);

export function OwnWalletPageContainer({
    accountId: stringAccountId,
    useWalletResource,
}: {
    accountId: string;
    useWalletResource: (accountId: AccountId) => ResourceLike<OwnWalletResource>;
}) {
    const accountId = useMemo(() => Decoder.run(AccountIdFromString, stringAccountId).orThrow(), [stringAccountId]);
    const {data, isError, isLoading} = useWalletResource(accountId);

    if (isLoading || isError) {
        return <LoadingPage />;
    }
    if (!data) {
        return <NotFoundPage />;
    }
    return <OwnWalletPage {...data} />;
}

function OwnWalletPage({currency, address, icon}: OwnWalletResource) {
    const [copied, setCopied] = useBoolean();

    const onAddressClick = async () => {
        if (copied) {
            return;
        }
        await copyToClipboard(address);
        setCopied.on();
        setTimeout(setCopied.off, 2000);
    };

    return (
        <RootLayout common title={`Receive ${currency}`} background='none'>
            <div className={cls.qrCodeBox}>
                <QRCodeSVG value={address} level='H' bgColor={color.neutral20} className={cls.qrCode} size={214} />
                {icon && <Img className={cls.qrIcon} src={icon} />}
            </div>

            <button className={cls.addressBox} onClick={onAddressClick}>
                <div className='flex flex-1 flex-col gap-2 p-4'>
                    <div className='fv-text-m text-neutral-60'>Your {currency} address</div>
                    <div className='fv-text-xl break-all text-neutral-100'>{address}</div>
                </div>
                <div className='grid place-items-center p-4'>
                    <RsnCopyIcon copied={copied} size='5' color='primary-30' />
                </div>
            </button>

            <div className='flex flex-col gap-2'>
                {[...stablecoins, 'ETH'].includes(currency) && (
                    <RsnMessage.FootnoteV2 centered iconSrc={ethCoinSvg}>
                        {`Raison supports the ERC-20 network on Ethereum blockchain. \nDeposits sent from other networks could be lost.`}
                    </RsnMessage.FootnoteV2>
                )}
                {stablecoins.includes(currency) && (
                    <RsnMessage.FootnoteV2 centered>
                        {`Limited functionality: the wallet supports only deposits, withdrawals and exchange operations to/from EUR with stable coins.`}
                    </RsnMessage.FootnoteV2>
                )}
            </div>
        </RootLayout>
    );
}

function Img(props: React.ComponentProps<'img'>) {
    const [errored, setErrored] = useState(false);
    if (errored) {
        props = {...props, style: {visibility: 'hidden'}};
    }
    return <img {...props} onError={() => setErrored(true)} />;
}

const sharedCls = styles({
    gridPileItem: '[grid-area:1/1]',
});
const cls = styles({
    qrCodeBox: 'grid place-items-center self-center rounded-lg border border-solid border-neutral-40 p-6',
    qrCode: `${sharedCls.gridPileItem}`,
    qrIcon: `${sharedCls.gridPileItem} box-content size-12 rounded-full bg-neutral-20 p-[5px]`,
    addressBox: 'flex rounded-lg bg-neutral-10 text-left',
});

function LoadingPage() {
    return (
        <RootLayout
            common
            background='none'
            pageHeader={
                <RootLayout.BaseHeader>
                    <RootLayout.MenuButton />
                    <Skeleton width={96} />
                    <RootLayout.BackButton />
                </RootLayout.BaseHeader>
            }
        >
            <div className={cls.qrCodeBox}>
                <Skeleton className={cls.qrCode} width={214} height={214} />
            </div>
            <div className={cls.addressBox}>
                <div className='flex flex-1 flex-col gap-2 p-4'>
                    <Skeleton height={12} width={96} />
                    <Skeleton height={16} width={'100%'} />
                    <Skeleton height={16} width={78} />
                </div>
                <div className='grid place-items-center p-4'>
                    <Skeleton circle width={16} />
                </div>
            </div>
        </RootLayout>
    );
}
